import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EMPLOYMENT_EVENT_TYPES } from './employmentEventTypes';
import { EVENT_VALIDATION_STATES } from './employmentValidationStates';

dayjs.extend(utc);

const ymdOf = (value) => (value ? dayjs.utc(value).format('YYYY-MM-DD') : null);

/**
 * ADR-176 — which events the Edit Amendment dialog may open on. Mirrors the
 * server's rule to HIDE the control where it would be refused; the server
 * stays authoritative (and also checks the contract and its owner).
 *
 * A Pending, unprocessed contract amendment — but not a scheduled position
 * change, which the position history owns (ADR-143 §9).
 */
export const canReviseAmendmentEvent = (event) => {
  if (!event || event.eventType?.code !== EMPLOYMENT_EVENT_TYPES.CONTRACT_AMENDED) {
    return false;
  }
  if (event.eventValidationState !== EVENT_VALIDATION_STATES.PENDING || event.processedAt) {
    return false;
  }
  const changes = event.payload?.changes;
  if (!changes || typeof changes !== 'object' || Object.keys(changes).length === 0) {
    return false;
  }
  return !('positionId' in changes);
};

export const trimmed = (value) => (typeof value === 'string' ? value.trim() : value);

// The editable state, read from the stored amendment.
export const initialRevisionStateOf = (event) => {
  const payload = event?.payload || {};
  return {
    changes: { ...(payload.changes || {}) },
    effectiveYmd: ymdOf(event?.effectiveOn),
    signedYmd: payload.signedOn || null,
    reason: payload.reason || '',
    externalReference: payload.externalReference || ''
  };
};

/**
 * The request body: only what differs from the stored amendment, so the
 * audit row's before/after names exactly what the operator changed.
 */
export const revisionBodyOf = (initial, current, revisionReason) => {
  const body = { revisionReason: trimmed(revisionReason) };
  const changesMoved = Object.keys(initial.changes).some(
    (field) => trimmed(current.changes[field]) !== initial.changes[field]
  );
  if (changesMoved) {
    body.changes = Object.fromEntries(
      Object.entries(current.changes).map(([field, value]) => [field, trimmed(value)])
    );
  }
  if (current.effectiveYmd !== initial.effectiveYmd) {
    body.effectiveOn = current.effectiveYmd;
  }
  if (current.signedYmd !== initial.signedYmd && current.signedYmd) {
    body.signedOn = current.signedYmd;
  }
  if (trimmed(current.reason) !== initial.reason) {
    body.reason = trimmed(current.reason);
  }
  if (trimmed(current.externalReference) !== initial.externalReference) {
    body.externalReference = trimmed(current.externalReference);
  }
  return body;
};
